import { useEffect, useMemo, useState } from 'react';
import { endpoints, type WorkflowBlueprint, type WorkflowNode, type WorkflowResult } from '../api';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Stat, Stats, Status, dateTime, number, searchText } from '../components';
import { useApiQuery } from '../hooks';
import './workflow.css';

const DEFAULT_OPEN_DEPTH = 2;
const ALL_DEPTHS = 99;

function imageVariant(blueprint: WorkflowBlueprint) {
  return blueprint.bpc > 0 && blueprint.bpo === 0 ? 'bpc' : 'bp';
}

export function TypeImage({ typeId, variant, size }: { typeId: number; variant: 'icon' | 'bp' | 'bpc'; size: number }) {
  return <img className="wf-image" src={`https://images.evetech.net/types/${typeId}/${variant}?size=64`} width={size} height={size} alt="" loading="lazy" onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }} />;
}

function BlueprintStatus({ blueprint }: { blueprint: WorkflowBlueprint }) {
  if (!blueprint.bpo && !blueprint.bpc) return <Status value="missing" label="Sin BP" />;
  const held = blueprint.bpo ? `BPO × ${number(blueprint.bpo)}` : `BPC × ${number(blueprint.bpc)} · ${number(blueprint.bpcRuns)} runs`;
  if (!blueprint.bpo && blueprint.bpcRuns < blueprint.runs) return <Status value="missing" label={`${held} · insuficiente`} />;
  if (!blueprint.available) return <Status value="ocupado" label={`${held} · no disponible`} />;
  return <Status value="disponible" label={held} />;
}

function ChainNode({ node, depth, openDepth, root = false }: { node: WorkflowNode; depth: number; openDepth: number; root?: boolean }) {
  const [open, setOpen] = useState(depth < openDepth);
  const { blueprint, children } = node;
  const tone = node.missing === 0 ? 'covered' : blueprint && (blueprint.bpo || blueprint.bpc) ? 'build' : 'missing';
  return <div className="wf-branch">
    <article className={`wf-node wf-node--${tone}`}>
      <div className="wf-node-head">
        <TypeImage typeId={node.typeId} variant="icon" size={32} />
        <div className="wf-node-title"><strong>{node.name}</strong><small className="mono">{root ? `${number(node.required)} uds.` : <>{number(node.required)} req. · {number(node.covered)} cub. · {node.missing > 0 ? <b className="wf-missing">{number(node.missing)} faltan</b> : '✓ completo'}</>}</small></div>
        {children.length > 0 && <button type="button" className="expand wf-toggle" aria-expanded={open} aria-label={`${open ? 'Contraer' : 'Expandir'} materiales de ${node.name}`} onClick={() => setOpen(!open)}>▶</button>}
      </div>
      {blueprint && <div className="wf-node-bp">
        <TypeImage typeId={blueprint.typeId} variant={imageVariant(blueprint)} size={24} />
        <BlueprintStatus blueprint={blueprint} />
        <small className="mono">{blueprint.runs > 0 ? `${number(blueprint.runs)} × ${number(blueprint.outputPerRun)}` : 'sin job'}{blueprint.activity === 'manufacturing' ? ` · ME ${blueprint.materialEfficiency}` : ' · reacción'}</small>
        {!root && <a className="link" href={`#/workflow?blueprint=${blueprint.typeId}&runs=${Math.max(1, blueprint.runs)}`}>Abrir</a>}
      </div>}
    </article>
    {open && children.length > 0 && <ul className="wf-children">{children.map((child, index) => <li key={`${child.typeId}-${index}`}><ChainNode node={child} depth={depth + 1} openDepth={openDepth} /></li>)}</ul>}
  </div>;
}

export function WorkflowPage() {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const blueprints = useApiQuery(endpoints.blueprints);
  const catalog = useApiQuery(endpoints.workflowCatalog);
  const [scope, setScope] = useState(params.has('blueprint') ? 'catalog' : 'owned');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState(params.get('blueprint') ? `type:${params.get('blueprint')}` : '');
  const [runs, setRuns] = useState(params.get('runs') ?? '1');
  const [me, setMe] = useState('');
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [openDepth, setOpenDepth] = useState(DEFAULT_OPEN_DEPTH);
  const [result, setResult] = useState<WorkflowResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  const term = searchText(search);
  const ownedOptions = useMemo(() => (blueprints.data ?? []).filter((bp) => bp.itemId && searchText(`${bp.name} ${bp.locationName ?? ''}`).includes(term)).sort((a, b) => a.name.localeCompare(b.name)), [blueprints.data, term]);
  const catalogOptions = useMemo(() => (catalog.data ?? []).filter((item) => searchText(item.name).includes(term)), [catalog.data, term]);
  // Keep a blueprint opened from a link selectable when the search would hide it.
  const selectedRecipe = catalog.data?.find((item) => `type:${item.blueprintTypeId}` === selection);
  const visibleCatalog = catalogOptions.slice(0, 100);
  if (selectedRecipe && !visibleCatalog.includes(selectedRecipe)) visibleCatalog.unshift(selectedRecipe);
  const blueprintId = selection.startsWith('item:') ? blueprints.data?.find((bp) => String(bp.itemId) === selection.slice(5))?.typeId : selection.startsWith('type:') ? Number(selection.slice(5)) : undefined;
  const validRuns = Number.isInteger(Number(runs)) && Number(runs) >= 1 && Number(runs) <= 100000;
  const validMe = me === '' || (Number.isInteger(Number(me)) && Number(me) >= 0 && Number(me) <= 10);

  useEffect(() => {
    if (!blueprintId || !validRuns || !validMe) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    const timer = window.setTimeout(() => {
      void endpoints.workflow({ blueprintTypeId: blueprintId, runs: Number(runs), ...(me === '' ? {} : { me: Number(me) }), ownedOnly })
        .then((value) => { if (!cancelled) setResult(value); })
        .catch((caught: Error) => { if (!cancelled) { setResult(null); setError(caught); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [blueprintId, runs, me, ownedOnly, validRuns, validMe, attempt]);

  function select(value: string) {
    setSelection(value);
    setResult(null);
    const owned = value.startsWith('item:') ? blueprints.data?.find((bp) => String(bp.itemId) === value.slice(5)) : undefined;
    setMe(owned ? String(owned.materialEfficiency) : '');
  }

  const inChain = result?.blueprints ?? [];
  const missingBlueprints = inChain.filter((bp) => !bp.bpo && !bp.bpc);
  const treeKey = result ? `${result.root.typeId}:${runs}:${me}:${ownedOnly}:${openDepth}` : '';

  return <Page title="Workflow" actions={loading ? <small role="status">Calculando…</small> : undefined}>
    <Section>
      <div className="filters">
        <Field label="Origen"><select value={scope} onChange={(event) => { setScope(event.target.value); setSearch(''); select(''); }}><option value="owned">Mis blueprints</option><option value="catalog">Catálogo SDE</option></select></Field>
        <Field label="Buscar" wide><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Blueprint o fórmula" /></Field>
        <Field label="Blueprint" wide hint={scope === 'catalog' ? `${number(catalogOptions.length)} recetas; se muestran 100` : undefined}><select value={selection} onChange={(event) => select(event.target.value)}>
          <option value="">Selecciona</option>
          {scope === 'owned'
            ? ownedOptions.map((bp) => <option key={bp.itemId} value={`item:${bp.itemId}`}>{bp.name} · {bp.original ? 'BPO' : `BPC ${bp.runs ?? '?'} runs`} · ME {bp.materialEfficiency}/TE {bp.timeEfficiency}{bp.activeJobId ? ' · OCUPADO' : ''}</option>)
            : visibleCatalog.map((item) => <option key={item.blueprintTypeId} value={`type:${item.blueprintTypeId}`}>{item.name}</option>)}
        </select></Field>
        <Field label="Runs"><input type="number" min="1" max="100000" aria-invalid={!validRuns} value={runs} onChange={(event) => setRuns(event.target.value)} /></Field>
        <Field label="ME raíz" hint="Vacío: mejor BP propio"><input type="number" min="0" max="10" aria-invalid={!validMe} value={me} onChange={(event) => setMe(event.target.value)} /></Field>
      </div>
      {blueprints.error && <ErrorState error={blueprints.error} onRetry={() => void blueprints.reload()} />}
      {catalog.error && <ErrorState error={catalog.error} onRetry={() => void catalog.reload()} />}
      {error && <ErrorState error={error} onRetry={() => setAttempt(attempt + 1)} />}
      {!blueprintId && !error && <EmptyState title="Selecciona un BPO, BPC o fórmula" />}
      {loading && !result && !error && <LoadingState label="Calculando la cadena…" />}
    </Section>

    {result && <div className="wf-result">
      {!result.stockCurrent && <Note tone="warn">Activos caducados o sin sincronizar ({dateTime(result.stockFetchedAt)}): el stock cubierto puede no ser real.</Note>}
      <Stats>
        <Stat label="Tipos a comprar" value={number(result.buy.length)} />
        <Stat label="BP en la cadena" value={number(inChain.length)} detail={`${number(inChain.length - missingBlueprints.length)} en propiedad`} />
        <Stat label="BP que no tienes" value={number(missingBlueprints.length)} tone={missingBlueprints.length ? 'neg' : undefined} />
      </Stats>

      <Section title="Cadena de producción" action={<div className="wf-actions">
        <div className="segments" role="group" aria-label="Alcance de la cadena">
          <button type="button" aria-pressed={!ownedOnly} onClick={() => setOwnedOnly(false)}>Completa</button>
          <button type="button" aria-pressed={ownedOnly} onClick={() => setOwnedOnly(true)}>Solo mis BP</button>
        </div>
        <Button type="button" variant="ghost" onClick={() => setOpenDepth(ALL_DEPTHS)}>Expandir todo</Button>
        <Button type="button" variant="ghost" onClick={() => setOpenDepth(openDepth === 1 ? 0 : 1)}>Contraer</Button>
      </div>}>
        <div className="wf-tree" role="region" aria-label="Árbol de producción" tabIndex={0}><ChainNode key={treeKey} node={result.root} depth={0} openDepth={openDepth} root /></div>
        <p className="fine">Borde grueso rojo: falta sin BP o sin receta. Borde grueso azul: falta y tienes el BP. Borde fino verde: completo. Cantidades con ME del BP, sin bonus de estructura. Cubierto: stock en hangar y contenedores de estación de cualquier ubicación más excedente de jobs anteriores de la cadena, asignado en orden del árbol.</p>
      </Section>

      <div className="wf-summary">
        <Section title="Comprar">
          {result.buy.length === 0 ? <p className="muted">Nada: el stock cubre la cadena.</p> : <div className="table-wrap"><table><thead><tr><th>Material</th><th className="num">Cantidad</th></tr></thead><tbody>{result.buy.map((item) => <tr key={item.typeId}>
            <td><span className="wf-cell"><TypeImage typeId={item.typeId} variant="icon" size={24} />{item.name}</span></td>
            <td className="num">{number(item.quantity)}</td>
          </tr>)}</tbody></table></div>}
        </Section>
        <Section title="Blueprints">
          <div className="table-wrap"><table><thead><tr><th>Blueprint</th><th>Estado</th><th className="num">Runs</th><th /></tr></thead><tbody>{inChain.map((bp) => <tr key={bp.typeId}>
            <td><span className="wf-cell"><TypeImage typeId={bp.typeId} variant={imageVariant(bp)} size={24} />{bp.name}</span></td>
            <td><BlueprintStatus blueprint={bp} /></td>
            <td className="num">{number(bp.runs)}</td>
            <td className="num">{bp.activity === 'manufacturing' && <a className="link" href={`#/industry?blueprint=${bp.typeId}&runs=${Math.max(1, bp.runs)}`}>Calcular</a>}</td>
          </tr>)}</tbody></table></div>
        </Section>
      </div>
    </div>}
  </Page>;
}
